import { useState } from 'react';
import type { PublicPlayer, Snapshot } from 'shared';
import { Avatar } from '../design/Avatar';
import { Button } from '../design/Button';
import { Panel } from '../design/Panel';
import { Label, Rule } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { LinkRow } from '../components/LinkRow';
import { ro } from '../i18n/ro';
import { useRoom } from '../state/RoomConnection';
import { joinLinkFor, projectorLinkFor } from '../state/storage';

/**
 * The admin's controls. Dense on purpose — UX effort is spent on players, and
 * the admin wrote the app.
 *
 * Every action here is a *corrective* one: it changes state quietly, produces no
 * history entry and triggers no announcement. The projector simply re-renders
 * the corrected truth.
 */
export function AdminPanel({ snapshot }: { snapshot: Snapshot }) {
  const room = useRoom();
  const p = snapshot.permissions;
  const [picking, setPicking] = useState<'PRESIDENT' | 'CHANCELLOR' | null>(null);
  const [president, setPresident] = useState<string | null>(null);
  const [chancellor, setChancellor] = useState<string | null>(null);
  const [correcting, setCorrecting] = useState(false);

  const alive = snapshot.players.filter((x) => x.alive);
  const ready = president !== null && chancellor !== null && president !== chancellor;

  function reset() {
    setPicking(null);
    setPresident(null);
    setChancellor(null);
    setCorrecting(false);
  }

  function submitGovernment() {
    if (!ready) return;
    room.send(
      correcting
        ? { type: 'setGovernment', presidentId: president!, chancellorId: chancellor! }
        : { type: 'startSession', presidentId: president!, chancellorId: chancellor! },
    );
    reset();
  }

  return (
    <section className="border-t-[3px] border-ink bg-paper-sunk px-4 pb-6 pt-4">
      <Label className="block text-[0.6rem] text-ink-soft">{ro.adminPanel}</Label>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button
          tone="ink"
          size="sm"
          block
          disabled={!p.canStartSession}
          data-testid="admin-start-vote"
          onClick={() => {
            setCorrecting(false);
            setPicking('PRESIDENT');
          }}
        >
          {ro.startVote}
        </Button>

        <Button
          tone="red"
          size="sm"
          block
          disabled={!p.canAnnulSession}
          data-testid="admin-annul"
          onClick={() => {
            if (window.confirm(ro.confirmAnnul)) room.send({ type: 'annulSession' });
          }}
        >
          {ro.annulVote}
        </Button>

        <Button
          tone="paper"
          size="sm"
          block
          disabled={!p.canSetGovernment}
          data-testid="admin-set-government"
          onClick={() => {
            setCorrecting(true);
            setPicking('PRESIDENT');
          }}
        >
          {ro.setGovernment}
        </Button>

        <Button
          tone="paper"
          size="sm"
          block
          disabled={!p.canProjectHistory && snapshot.projection === 'LIVE'}
          data-testid="admin-projection"
          onClick={() =>
            room.send({
              type: 'setProjection',
              mode: snapshot.projection === 'HISTORY' ? 'LIVE' : 'HISTORY',
            })
          }
        >
          {snapshot.projection === 'HISTORY' ? ro.showLive : ro.showHistory}
        </Button>
      </div>

      {picking && (
        <Panel press="sm" className="mt-4" innerClassName="p-3">
          <Label className="block text-[0.6rem] text-ink-soft">
            {picking === 'PRESIDENT' ? ro.choosePresident : ro.chooseChancellor}
          </Label>

          <div className="mt-3 flex flex-col gap-1">
            {snapshot.players.map((player) => (
              <PickRow
                key={player.id}
                player={player}
                snapshot={snapshot}
                selected={picking === 'PRESIDENT' ? president === player.id : chancellor === player.id}
                disabled={picking === 'CHANCELLOR' && president === player.id}
                onSelect={() => {
                  if (picking === 'PRESIDENT') {
                    setPresident(player.id);
                    setPicking('CHANCELLOR');
                  } else {
                    setChancellor(player.id);
                    setPicking(null);
                  }
                }}
              />
            ))}
          </div>

          <div className="mt-3 flex gap-2">
            <Button tone="ghost" size="sm" block onClick={reset}>
              ✕
            </Button>
          </div>
        </Panel>
      )}

      {!picking && (president || chancellor) && (
        <Panel press="sm" className="mt-4" innerClassName="p-3">
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between gap-3">
              <Label className="text-[0.6rem] text-ink-soft">{ro.president}</Label>
              <span className="truncate">{nameOf(snapshot, president)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <Label className="text-[0.6rem] text-ink-soft">{ro.chancellor}</Label>
              <span className="truncate">{nameOf(snapshot, chancellor)}</span>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              tone="ink"
              size="sm"
              block
              disabled={!ready}
              data-testid="admin-confirm-government"
              onClick={submitGovernment}
            >
              {correcting ? ro.setGovernment : ro.startVote}
            </Button>
            <Button tone="ghost" size="sm" onClick={reset}>
              ✕
            </Button>
          </div>
        </Panel>
      )}

      <Rule className="my-5" />

      <Label className="block text-[0.6rem] text-ink-soft">
        {ro.players} · {snapshot.players.length}
      </Label>
      <div className="mt-3 flex flex-col gap-2">
        {snapshot.players.map((player) => (
          <ManageRow key={player.id} player={player} snapshot={snapshot} />
        ))}
        {snapshot.players.length === 0 && (
          <p className="text-sm text-ink-faint">{ro.noPlayersYet}</p>
        )}
      </div>

      <Rule className="my-5" />

      {/* Kept here because the shared-screen URL is otherwise unrecoverable
          once the creation screen is gone, and the QR alone is not enough for
          someone who is not at the table. */}
      <details className="group">
        <summary className="cursor-pointer list-none">
          <Label className="text-[0.6rem] text-ink-soft">
            {ro.links} ▾
          </Label>
        </summary>
        <div className="mt-3 flex flex-col gap-4">
          <LinkRow
            title={ro.joinLinkTitle}
            body={ro.joinLinkBody}
            url={joinLinkFor(room.roomToken, window.location.origin)}
            testId="join-link"
          />
          <LinkRow
            title={ro.projectorLinkTitle}
            body={ro.projectorLinkBody}
            url={projectorLinkFor(room.roomToken, window.location.origin)}
            testId="projector-link"
          />
        </div>
      </details>

      <Rule className="my-5" />

      <div className="grid grid-cols-2 gap-2">
        <Button
          tone="paper"
          size="sm"
          block
          disabled={!p.canReset}
          data-testid="admin-reset"
          onClick={() => {
            if (window.confirm(ro.confirmReset)) room.send({ type: 'resetRoom' });
          }}
        >
          {ro.resetRoom}
        </Button>
        <Button
          tone="red"
          size="sm"
          block
          disabled={!p.canClose}
          data-testid="admin-close"
          onClick={() => {
            if (window.confirm(ro.confirmClose)) room.send({ type: 'closeRoom' });
          }}
        >
          {ro.closeRoom}
        </Button>
      </div>
      {alive.length === 0 && snapshot.players.length > 0 && (
        <p className="mt-3 text-xs text-red">{ro.errors.NO_ELIGIBLE_VOTERS}</p>
      )}
    </section>
  );
}

function nameOf(snapshot: Snapshot, id: string | null): string {
  if (!id) return '—';
  return snapshot.players.find((p) => p.id === id)?.name ?? '—';
}

/** A candidate row in the picker: warns about term limits and executions, never blocks. */
function PickRow({
  player,
  snapshot,
  selected,
  disabled,
  onSelect,
}: {
  player: PublicPlayer;
  snapshot: Snapshot;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const termLimited = snapshot.termLimitedPlayerIds.includes(player.id);

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      data-testid="pick-row"
      data-name={player.name}
      className={`flex items-center gap-2.5 border-2 px-2 py-2 text-left transition-colors disabled:opacity-35 ${
        selected ? 'border-ink bg-paper-sunk' : 'border-transparent'
      }`}
    >
      <Avatar subject={player} size="sm" dimmed={!player.alive} />
      <span className="min-w-0 flex-1 truncate text-sm">{player.name}</span>
      {termLimited && (
        <Stamp tone="ink" size="sm" className="shrink-0">
          {ro.termLimited}
        </Stamp>
      )}
      {!player.alive && (
        <Stamp tone="red" size="sm" className="shrink-0">
          {ro.executed}
        </Stamp>
      )}
    </button>
  );
}

/** Roster management: execute, revive, kick, rename. */
function ManageRow({ player, snapshot }: { player: PublicPlayer; snapshot: Snapshot }) {
  const room = useRoom();
  const p = snapshot.permissions;

  return (
    <div
      className="flex items-center gap-2 border-b border-ink/15 pb-2 last:border-0"
      data-testid="manage-row"
      data-name={player.name}
    >
      <Avatar subject={player} size="sm" dimmed={!player.alive} />
      <button
        type="button"
        className="min-w-0 flex-1 truncate text-left text-sm underline decoration-ink/25 underline-offset-2"
        disabled={!p.canRename}
        onClick={() => {
          const next = window.prompt(ro.rename, player.name);
          if (next && next.trim()) {
            room.send({ type: 'renamePlayer', playerId: player.id, name: next });
          }
        }}
      >
        {player.name}
      </button>

      {player.alive ? (
        <Button
          tone="ghost"
          size="sm"
          disabled={!p.canExecute}
          data-testid="manage-execute"
          onClick={() => room.send({ type: 'executePlayer', playerId: player.id })}
        >
          {ro.execute}
        </Button>
      ) : (
        <Button
          tone="ghost"
          size="sm"
          disabled={!p.canExecute}
          data-testid="manage-revive"
          onClick={() => room.send({ type: 'revivePlayer', playerId: player.id })}
        >
          {ro.revive}
        </Button>
      )}

      {!player.isAdmin && (
        <Button
          tone="ghost"
          size="sm"
          disabled={!p.canKick}
          data-testid="manage-kick"
          onClick={() => room.send({ type: 'kickPlayer', playerId: player.id })}
        >
          ✕
        </Button>
      )}
    </div>
  );
}
